import { Injectable } from '@angular/core';
import { PlaylistTrack, Track } from '@app/core/models/Track';
import { TracksState } from '@app/library/states/tracks.state';
import { replaceAccents } from '@app/shared/utils';
import { Store } from '@ngxs/store';
import { Entry } from '@radioking/daily-generation';
import { SchedulerCoreState, SchedulerTrack } from '@radioking/scheduler-core';
import { flatten, isArray, isNil, trim, words } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs/operators';

type TrackAlike = Partial<Track & PlaylistTrack & SchedulerTrack & Entry>;

type SearchIndex<T> = Map<T, SearchIndexValue>;

interface SearchIndexValue {
  asString: string;
  asWords: string[];
}

/**
 * Field separator used when building search index
 */
const SEARCH_INDEX_FIELD_SEPARATOR = '\n';

/**
 * Single character used to search with an exact match
 */
const SEARCH_EXACT_MATCH_BOUNDARY = '"';

/**
 * Available search operators
 */
enum SEARCH_OPERATORS {
  OR,
  AND,
}

/**
 * Search operators regExps, used to split search terms and determine which operator will be used
 */
const searchOperatorsRegExps = {
  [SEARCH_OPERATORS.AND]: /[ ]+/g,
  [SEARCH_OPERATORS.OR]: /[ ]+(?:OR)[ ]+/g,
};

/**
 * User input debounce in milliseconds
 */
const SEARCH_DEBOUNCE_MS = 200;

/**
 * Process with legacy search (search terms matching start of words in indexed fields)
 * or not (search terms included in indexed fields)
 */
const IS_LEGACY_SEARCH = true;

/**
 * Tracks alike sort function
 */
const sortTracksAlike = <T extends TrackAlike>(a: T, b: T): number => {
  const aLower = a.title.toLowerCase();
  const bLower = b.title.toLowerCase();

  return aLower.localeCompare(bLower);
};

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private dropdownTracksSearchWorker$: Observable<{
    index: SearchIndex<TrackAlike>;
    tracks: TrackAlike[];
  }>;

  constructor(private readonly store: Store) {}

  /**
   * Normalize search terms
   *
   * - Case insensitive
   * - Special chars are normalized to NFD or removed (accents, etc)
   */
  termFilter(term: string): string {
    return replaceAccents(term.toLowerCase());
  }

  /**
   * Search user input in specified index
   */
  search<T extends TrackAlike>(
    tracks: T[],
    index: SearchIndex<T>,
    userInput: string,
  ): T[] {
    // Empty search (check with trimmed spaces and exact match boundaries)
    if (!userInput || trim(userInput, SEARCH_EXACT_MATCH_BOUNDARY).trim() === '') {
      return tracks;
    }

    // Cannot search field separator (it'd match everything)
    if (userInput.includes(SEARCH_INDEX_FIELD_SEPARATOR)) {
      return [];
    }

    // Build search terms
    const { searchOperator, searchTerms, isLegacySearch } =
      this.buildSearchTerms(userInput);
    const searchOperatorFunc = (
      searchOperator === SEARCH_OPERATORS.AND ? searchTerms.every : searchTerms.some
    ).bind(searchTerms);

    // Search in index
    return Array.from(index.entries())
      .filter(([, indexedSearch]) =>
        searchOperatorFunc(searchTerm =>
          this.searchTerm(indexedSearch, searchTerm, isLegacySearch),
        ),
      )
      .map(([track]) => track);
  }

  /**
   * Search term in an indexed search entry
   */
  searchTerm(
    indexedSearch: SearchIndexValue,
    searchTerm: string,
    isLegacySearch: boolean,
  ): boolean {
    if (isLegacySearch) {
      return indexedSearch.asWords.some(word => word.startsWith(searchTerm));
    }

    return indexedSearch.asString.includes(searchTerm);
  }

  /**
   * Build search terms from user input
   *
   * Can build:
   * - An "AND" combined search, combining words separated by a space with an "AND" condition.
   * - An "OR" combined search, combining words separated by an OR with an "OR" condition.
   * - An exact match search, like searching <"Nobuo Uematsu"> will match exact text instead of matching <Nobuo> AND <Uematsu>
   */
  buildSearchTerms(userInput: string): {
    searchOperator: SEARCH_OPERATORS;
    searchTerms: string[];
    isLegacySearch: boolean;
  } {
    const trimmedUserInput = userInput.trim();
    let searchOperator = SEARCH_OPERATORS.AND;
    let searchTerms: string[];
    let isLegacySearch = IS_LEGACY_SEARCH;

    if (
      trimmedUserInput.startsWith(SEARCH_EXACT_MATCH_BOUNDARY) &&
      trimmedUserInput.endsWith(SEARCH_EXACT_MATCH_BOUNDARY)
    ) {
      // Exact match
      searchTerms = [trim(trimmedUserInput, SEARCH_EXACT_MATCH_BOUNDARY)];
      isLegacySearch = false;
    } else if (trimmedUserInput.match(searchOperatorsRegExps[SEARCH_OPERATORS.OR])) {
      // "OR" combined search
      searchTerms = trimmedUserInput.split(searchOperatorsRegExps[SEARCH_OPERATORS.OR]);
      searchOperator = SEARCH_OPERATORS.OR;
    } else {
      // "AND" combined search
      searchTerms = trimmedUserInput.split(searchOperatorsRegExps[SEARCH_OPERATORS.AND]);
    }

    searchTerms = searchTerms.map(term => this.termFilter(term));

    if (isLegacySearch) {
      const searchTermsAsWords = flatten(searchTerms.map(s => words(s)));

      if (searchTermsAsWords.length) {
        searchTerms = searchTermsAsWords;
      }
    }

    return { searchOperator, searchTerms, isLegacySearch };
  }

  /**
   * Create a search index from tracks alike
   *
   * - Indexing fields "title", "artist", "album" by default
   * - Also indexing fields "tags", "playlists", "year" when basic=false
   */
  createSearchIndex<T extends TrackAlike>(tracks: T[], basic: boolean): SearchIndex<T> {
    return new Map(
      tracks.map(track => {
        const fields: (string | number)[] = [track.title, track.artist, track.album];

        if (!basic) {
          if (track.tags) {
            fields.push(...track.tags.map(t => t.name));
          }
          if (track.playlists) {
            fields.push(...track.playlists.map(p => p.name));
          }
          fields.push(track.year);
        }

        if (track.tagNames) {
          fields.push(...track.tagNames);
        }

        const str = fields
          .filter(x => !isNil(x) && x !== '')
          .join(SEARCH_INDEX_FIELD_SEPARATOR);

        const asString = this.termFilter(str);
        const asWords = IS_LEGACY_SEARCH ? words(asString) : [];

        return [track, { asString, asWords }];
      }),
    );
  }

  createTracksSearch<T extends TrackAlike>(
    data$: Observable<T[]>,
    searchInput$: Observable<string>,
    basic = false,
  ): Observable<T[]> {
    const searchInputDebounced$ = searchInput$.pipe(
      distinctUntilChanged(),
      debounceTime(SEARCH_DEBOUNCE_MS),
      startWith(''),
    );

    const dataWithIndex$ = data$.pipe(
      distinctUntilChanged(),
      filter(val => !!val),
      switchMap(tracks => {
        const index = this.createSearchIndex(tracks, basic);

        return of({ tracks, index });
      }),
    );

    return combineLatest([dataWithIndex$, searchInputDebounced$]).pipe(
      map(([{ tracks, index }, userInput]) => this.search(tracks, index, userInput)),
      startWith([]),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  createDropdownTracksSearch<T extends TrackAlike>(
    searchInput$: Observable<string>,
    schedulerSearch = false,
  ): Observable<T[]> {
    if (schedulerSearch) {
      this.generateDropdownTracksIndex(SchedulerCoreState.allTracks);
    } else {
      this.generateDropdownTracksIndex(TracksState.allTracksForDropdown);
    }

    const searchInputDebounced$ = searchInput$.pipe(
      distinctUntilChanged(),
      debounceTime(SEARCH_DEBOUNCE_MS),
      startWith(''),
    );

    return combineLatest([this.dropdownTracksSearchWorker$, searchInputDebounced$]).pipe(
      map(
        ([{ index, tracks }, userInput]) =>
          this.search(tracks, index, userInput).sort(sortTracksAlike) as T[],
      ),
      startWith([]),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private generateDropdownTracksIndex(selector: (state: any) => TrackAlike[]): void {
    if (this.dropdownTracksSearchWorker$) {
      return;
    }
    this.dropdownTracksSearchWorker$ = this.store.select(selector).pipe(
      filter(val => isArray(val)),
      distinctUntilChanged(),
      map(tracks => {
        const index = this.createSearchIndex(tracks, true);

        return { index, tracks };
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  createDailyGenSearch<T extends TrackAlike>(
    data$: Observable<T[]>,
    searchInput$: Observable<string>,
  ): Observable<T[]> {
    const searchInputDebounced$ = searchInput$.pipe(
      distinctUntilChanged(),
      debounceTime(SEARCH_DEBOUNCE_MS),
      startWith(''),
    );

    const dataWithIndex$ = data$.pipe(
      distinctUntilChanged(),
      filter(val => !!val),
      switchMap(tracks => {
        const index = this.createDailyGenSearchIndex(tracks);

        return of({ tracks, index });
      }),
    );

    return combineLatest([dataWithIndex$, searchInputDebounced$]).pipe(
      map(([{ tracks, index }, userInput]) => this.search(tracks, index, userInput)),
      startWith([]),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  createDailyGenSearchIndex<T extends TrackAlike>(entries: T[]): SearchIndex<T> {
    return new Map(
      entries.map(entry => {
        const fields: (string | number)[] = [
          entry.track.title,
          entry.track.artist,
          entry.track.album,
        ];

        const str = fields
          .filter(x => !isNil(x) && x !== '')
          .join(SEARCH_INDEX_FIELD_SEPARATOR);

        const asString = this.termFilter(str);
        const asWords = IS_LEGACY_SEARCH ? words(asString) : [];

        return [entry, { asString, asWords }];
      }),
    );
  }
}
